import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'
import debounce from 'lodash/debounce'

import { InputBaseProps } from '@material-ui/core/InputBase/InputBase'
import { Grow, List, MenuItem, Paper } from '@material-ui/core'

import { BootstrapInput } from './BootstrapInput'
import { ProperClickAwayListener } from './ProperClickAwayListener'

import { useBoolState } from '../hooks/commonHooks'
import { useDefaultMenuItemStyles } from '../hooks/defaultSelectStylesHook'

import { emDash } from '../common/stringUtility'

export type CustomSelectOption<T> = {
  value: T | undefined

  label: string
}

export type CustomSelectProps<T> = {
  value: T | undefined

  onChange: (value: T | undefined) => void | Promise<void>

  options: CustomSelectOption<T>[]

  fullWidth?: boolean

  placeholder?: string

  onInputChange?: (newSearchTerm: string) => void

  readOnly?: boolean

  searchable?: boolean

  isLoading?: boolean

  withUnselectedValue?: boolean

  // NOTE: These are easy to mishandle, pick only what is required
  inputProps?: Pick<InputBaseProps, 'className' | 'disabled'>
}

export const DefaultSelect =
  observer(
    <T extends unknown>
    ({
       value,
       onChange,
       options,
       fullWidth = false,
       placeholder = '',
       onInputChange,
       readOnly = false,
       searchable = false,
       isLoading = false,
       withUnselectedValue = false,
       inputProps = {}
     }: CustomSelectProps<T>) => {
      const classes = useDefaultMenuItemStyles()
      const [isOpen, setOpen, setClosed, toggleOpen] = useBoolState()
      const [inputValue, setInputValue] = useState('')
      const [isLoadingSelect, setLoadingSelect, setNotLoadingSelect] = useBoolState()

      // TODO: Why are we re-implementing all of this instead of styling MUI components? There are a lot of complications with these inputs.
      const handleInputChangeDebounced =
        useMemo(() => debounce(
            (newValue: string) => {
              if (searchable && onInputChange) {
                onInputChange(newValue)
              }
            }, 800),
          [onInputChange, searchable])

      useEffect(() => {
          const selectedOption = options.find(option => option.value === value)

          if (selectedOption) {
            setInputValue(selectedOption.label)
          } else if (withUnselectedValue) {
            setInputValue(emDash)
          }
        },
        [value, options, withUnselectedValue])

      const handleToggle =
        useCallback(
          (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()

            toggleOpen()
          }, [toggleOpen])

      const handleSelect =
        useCallback(
          async (e: React.MouseEvent, selectedValue: T | undefined) => {
            e.preventDefault()
            e.stopPropagation()

            if (selectedValue === value) {
              setClosed()
              return
            }

            setLoadingSelect()

            try {
              await onChange(selectedValue)
            } finally {
              setNotLoadingSelect()
              setClosed()
            }
          },
          [value, onChange, setClosed, setLoadingSelect, setNotLoadingSelect])

      const handleInputChange =
        useCallback(
          ({target: {value: newValue}}: React.ChangeEvent<HTMLInputElement>) => {
            setInputValue(newValue)
            handleInputChangeDebounced(newValue)
          },
          [handleInputChangeDebounced])

      const handleKeyDown =
        useCallback(
          ({key}: React.KeyboardEvent) => {
            if (key === 'Escape') {
              setClosed()
            } else if (!isOpen && (key === 'ArrowDown' || key === 'Enter')) {
              setOpen()
            }
          },
          [isOpen, setClosed, setOpen])

      const finalOptions = withUnselectedValue
                           ? [{value: undefined, label: emDash}, ...options]
                           : options

      return (
        <div className={classes.inputWrapper} style={fullWidth ? {width: '100%'} : undefined}>
          <BootstrapInput
            fullWidth={fullWidth}
            value={inputValue}
            onClick={!readOnly ? handleToggle : undefined}
            onKeyDown={!readOnly ? handleKeyDown : undefined}
            onChange={!readOnly && searchable ? handleInputChange : undefined}
            readOnly={readOnly || !searchable}
            disabled={inputProps.disabled}
            placeholder={placeholder}
            className={clsx(classes.input, classes.inputClickable, inputProps.className)}
          />

          {
            isOpen &&
            <ProperClickAwayListener isOpen={isOpen} onClose={setClosed}>
              <Grow in={isOpen} style={{transformOrigin: 'top'}}>
                <Paper className={classes.menu}>
                  <List component="nav">
                    {
                      isLoading || isLoadingSelect
                      ?
                      <MenuItem className={classes.menuItem} disabled>
                        Загрузка...
                      </MenuItem>
                      :
                      finalOptions.length === 0
                      ?
                      <MenuItem className={classes.menuItem} disabled>
                        Нет доступных вариантов
                      </MenuItem>
                      : finalOptions.map((option, index) =>
                        <MenuItem
                          key={index}
                          className={classes.menuItem}
                          selected={option.value === value}
                          onClick={e => handleSelect(e, option.value)}
                        >
                          {option.label}
                        </MenuItem>)
                    }
                  </List>
                </Paper>
              </Grow>
            </ProperClickAwayListener>
          }
        </div>
      )
    })